/**
 * Universal Button Component
 *
 * A comprehensive button component using the KRP Design System.
 * Supports multiple variants, sizes, and states with consistent styling.
 *
 * @example
 * <Button variant="primary" size="md">Save</Button>
 * <Button variant="outline" icon={<RiAddLine />}>Add Item</Button>
 * <Button variant="danger" loading>Deleting...</Button>
 */

import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../utils/cn';

// ============================================================================
// TYPES
// ============================================================================

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'danger'
  | 'success'
  | 'warning'
  | 'info'
  | 'link';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant */
  variant?: ButtonVariant;
  /** Size variant */
  size?: ButtonSize;
  /** Show loading state */
  loading?: boolean;
  /** Text to show when loading */
  loadingText?: string;
  /** Icon to show on the left */
  icon?: React.ReactNode;
  /** Icon to show on the right */
  iconRight?: React.ReactNode;
  /** Make button full width */
  fullWidth?: boolean;
  /** Make button rounded (pill shape) */
  rounded?: boolean;
  /** Remove padding (for icon-only buttons) */
  iconOnly?: boolean;
  /** Additional class names */
  className?: string;
}

// ============================================================================
// VARIANT STYLES
// ============================================================================

const variantStyles: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-primary-600 text-white',
    'hover:bg-primary-700',
    'active:bg-primary-800',
    'focus:ring-primary-500',
    'shadow-button hover:shadow-button-hover'
  ),
  secondary: cn(
    'bg-secondary-100 text-secondary-700',
    'border border-secondary-200',
    'hover:bg-secondary-200',
    'active:bg-secondary-300',
    'focus:ring-secondary-400'
  ),
  outline: cn(
    'bg-transparent text-primary-600',
    'border border-primary-300',
    'hover:bg-primary-50',
    'active:bg-primary-100',
    'focus:ring-primary-500'
  ),
  ghost: cn(
    'bg-transparent text-secondary-600',
    'hover:bg-secondary-100',
    'active:bg-secondary-200',
    'focus:ring-secondary-400'
  ),
  danger: cn(
    'bg-danger-600 text-white',
    'hover:bg-danger-700',
    'active:bg-danger-800',
    'focus:ring-danger-500',
    'shadow-button hover:shadow-button-hover'
  ),
  success: cn(
    'bg-success-700 text-white',
    'hover:bg-success-800',
    'active:bg-success-800',
    'focus:ring-success-500',
    'shadow-button hover:shadow-button-hover'
  ),
  warning: cn(
    'bg-warning-500 text-white',
    'hover:bg-warning-600',
    'active:bg-warning-700',
    'focus:ring-warning-400',
    'shadow-button hover:shadow-button-hover'
  ),
  info: cn(
    'bg-info-500 text-white',
    'hover:bg-info-600',
    'active:bg-info-700',
    'focus:ring-info-400',
    'shadow-button hover:shadow-button-hover'
  ),
  link: cn(
    'bg-transparent text-primary-600',
    'hover:text-primary-700 hover:underline',
    'active:text-primary-800',
    'focus:ring-0',
    'px-0'
  ),
};

// ============================================================================
// SIZE STYLES
// ============================================================================

const sizeStyles: Record<ButtonSize, string> = {
  xs: 'min-h-[44px] sm:min-h-0 h-auto sm:h-7 px-2.5 text-xs gap-1',
  sm: 'min-h-[44px] sm:min-h-0 h-auto sm:h-8 px-3 text-sm gap-1.5',
  md: 'min-h-[44px] sm:min-h-0 h-auto sm:h-10 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-base gap-2',
  xl: 'h-12 px-6 text-lg gap-2.5',
};

const iconOnlySizeStyles: Record<ButtonSize, string> = {
  xs: 'h-7 w-7',
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-11 w-11',
  xl: 'h-12 w-12',
};

// ============================================================================
// LOADING SPINNER
// ============================================================================

const LoadingSpinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={cn('animate-spin', className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

// ============================================================================
// COMPONENT
// ============================================================================

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingText,
      icon,
      iconRight,
      fullWidth = false,
      rounded = false,
      iconOnly = false,
      className,
      children,
      disabled,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    // Determine icon size based on button size
    const iconSizeClass = {
      xs: 'w-3.5 h-3.5',
      sm: 'w-4 h-4',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
      xl: 'w-5 h-5',
    }[size];

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center',
          'font-medium',
          'transition-all duration-150 ease-out',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
          // Shape
          rounded ? 'rounded-full' : 'rounded-lg',
          // Size
          iconOnly ? iconOnlySizeStyles[size] : sizeStyles[size],
          // Variant
          variantStyles[variant],
          // Full width
          fullWidth && 'w-full',
          // Custom className
          className
        )}
        {...props}
      >
        {/* Loading state */}
        {loading && (
          <LoadingSpinner className={cn(iconSizeClass, children && '-ml-0.5')} />
        )}

        {/* Left icon */}
        {!loading && icon && (
          <span className={cn(iconSizeClass, 'flex-shrink-0')}>{icon}</span>
        )}

        {/* Button text */}
        {!iconOnly && (
          <span className={loading ? 'ml-1' : ''}>
            {loading ? loadingText || children : children}
          </span>
        )}

        {/* Right icon */}
        {!loading && iconRight && (
          <span className={cn(iconSizeClass, 'flex-shrink-0')}>{iconRight}</span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

// ============================================================================
// PRESET VARIANTS
// ============================================================================

/**
 * Primary action button
 */
export const PrimaryButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'variant'>
>((props, ref) => <Button ref={ref} variant="primary" {...props} />);
PrimaryButton.displayName = 'PrimaryButton';

/**
 * Secondary action button
 */
export const SecondaryButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'variant'>
>((props, ref) => <Button ref={ref} variant="secondary" {...props} />);
SecondaryButton.displayName = 'SecondaryButton';

/**
 * Danger/Delete button
 */
export const DangerButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'variant'>
>((props, ref) => (
  <Button ref={ref} variant="danger" loadingText="กำลังลบ..." {...props} />
));
DangerButton.displayName = 'DangerButton';

/**
 * Success button
 */
export const SuccessButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'variant'>
>((props, ref) => <Button ref={ref} variant="success" {...props} />);
SuccessButton.displayName = 'SuccessButton';

/**
 * Ghost/Transparent button
 */
export const GhostButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'variant'>
>((props, ref) => <Button ref={ref} variant="ghost" {...props} />);
GhostButton.displayName = 'GhostButton';

/**
 * Icon-only button
 */
export const IconButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ icon, children, ...props }, ref) => (
    <Button ref={ref} iconOnly icon={icon || children} {...props} />
  )
);
IconButton.displayName = 'IconButton';

// ============================================================================
// EXPORTS
// ============================================================================

export default Button;
